import React, { useCallback, useState } from 'react';
import { compose } from 'recompose';
import { hot } from 'react-hot-loader';
import styled from 'styled-components';
import { Sidebar, Button, Menu, Icon, Header, Input, Breadcrumb, Segment, Message, Label } from 'semantic-ui-react';

import { Offline, withOnlineStatus } from 'op/components/offline';
import { container } from 'op/container';
import { withLocale } from 'op/components/locale';
import { withAxiosHeaders } from 'op/components/auth';
import { withAction } from 'op/components/permission';

import Errors from 'op/components/error';
import SettingsModal from 'op/components/settings';
import ChangePasswordModal from 'op/components/ChangePassword';

import MachineTestsIcon from '../../assets/machine-tests.svg';

const Viewport = styled.div`
	height: 100vh;
	background: ${props => props.theme.white};

	overflow-x: hidden;
	overflow-y: auto;

	direction: ${props => !!props.rtl ? 'rtl' : 'ltr'} }
`;

const ViewportContent = styled.div`
	padding: 0 1em 2em 1em;

	@media (min-width: 768px) {
    padding: 0 3em 5em 3em;
  }
`;

const UserInfo = styled.div`
	padding: 30pt 10pt;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	color: white;
	border-bottom: solid gray 1pt;
`;

const OfflineContainer = styled.div`
	justify-self: flex-end;
`;

const ReportEditPermited = withAction('report', 2)(styled.div``);

const PageComponent = ({ locale, user, children, online }) => {
    const [sidebarOpen, setSidebarOpen] = useState(false);

    const navigate = useCallback((url) => {
        setSidebarOpen(false);
        container.resolve('history').push(url);
    }, [setSidebarOpen]);

    return (
        <Sidebar.Pushable>
            <Sidebar as={Menu} animation='uncover' direction={locale._meta.rtl ? 'right' : 'left'} width='thin'
                visible={sidebarOpen} icon='labeled' vertical inverted>
                <UserInfo>
                    <Icon name="user circle" size='huge' inverted />
                    <label>{user.username}</label>
                </UserInfo>
                <Menu.Item name='people' as='a' onClick={() => navigate('/')} active={location.pathname === '/'}>
                    <Icon name='users' />
                    {locale.sidebar.search}
                </Menu.Item>
                <Menu.Item style={{ display: 'flex' }} name='stp' as='a' onClick={() => navigate('/stp')}
                    active={location.pathname === '/stp'}>
                    <i>
                        <img src={MachineTestsIcon} style={{ height: '3em' }} />
                    </i>
                    {locale.sidebar.stp}
                </Menu.Item>
                <Menu.Item name='forms' as='a' onClick={() => navigate('/forms')}
                    active={location.pathname === '/forms'}>
                    <Icon name='bars' />
                    {locale.sidebar.forms}
                </Menu.Item>
                <Menu.Item name='audit' as='a' onClick={() => navigate('/audit')}
                    active={location.pathname === '/audit'}>
                    <Icon name='clipboard check' />
                    {locale.sidebar.audit}
                </Menu.Item>
                <ReportEditPermited>
                    <Menu.Item name='pending' as='a' onClick={() => navigate('/pending')}
                        active={location.pathname === '/pending'}>
                        <Icon name='time' />
                        {locale.sidebar.pending}
                    </Menu.Item>
                </ReportEditPermited>
                <SettingsModal trigger={(trigger) => (
                    <Menu.Item name='settings' as='a' onClick={() => {
                        setSidebarOpen(false);
                        trigger();
                    }}>
                        <Icon name='settings' />
                        {locale.sidebar.settings}
                    </Menu.Item>
                )} />
                <ChangePasswordModal locale={locale} trigger={(trigger) => (
                    <Menu.Item name='ChangePassword' as='a' onClick={() => {
                        setSidebarOpen(false);
                        trigger();
                    }}>
                        <Icon name='key' />
                        {locale.sidebar.changePassword}
                    </Menu.Item>
                )} />
                <Menu.Item name='logout' as='a' disabled={true} onClick={() => navigate('/logout')}>
                    <Icon name='sign out' />
                    {locale.sidebar.logout}
                </Menu.Item>
            </Sidebar>
            <Sidebar.Pusher>
                <Viewport rtl={locale._meta.rtl} onClick={() => sidebarOpen && setSidebarOpen(!sidebarOpen)}>
                    <Menu pointing secondary size='large'>
                        <Menu.Item onClick={() => setSidebarOpen(!sidebarOpen)}>
                            <Icon name='bars' />
                        </Menu.Item>
                        <Menu.Item name='home' onClick={() => navigate('/')}>OpeReady</Menu.Item>
                        <Menu.Item name='people' onClick={() => navigate('/')}>
                            <Icon name='search' />
                        </Menu.Item>
                        <Offline>
                            <Menu.Menu position='right'>
                                <Menu.Item>
                                    <Label color='red'>Offline</Label>
                                </Menu.Item>
                            </Menu.Menu>
                        </Offline>
                    </Menu>
                    <ViewportContent>
                        {children}
                    </ViewportContent>
                    <Errors />
                </Viewport>
            </Sidebar.Pusher>
        </Sidebar.Pushable>
    );
}

export default compose(
    hot(module),
    withAxiosHeaders,
    withOnlineStatus,
    withLocale('navigation'),
)(PageComponent);